import { useEffect, useState } from "react";
import axios from "axios";
import { Watch, ArrowLeft, Mail, Lock, LogOut, Loader2 } from "lucide-react";
import { COLORS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "trnscndnc_admin_token";

function Login({ onAuth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuth(data.token);
    } catch (err) {
      setState("idle");
      setError(err?.response?.data?.detail || "Login failed. Please try again.");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FBFBF9] px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-[3px] border border-[#0A0A0A]/8 bg-white p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] md:p-10">
        <div className="mb-8 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0A0A0A] text-white">
            <Lock size={15} />
          </span>
          <span className="font-serif-display text-xl text-[#0A0A0A]">Admin access</span>
        </div>
        <label htmlFor="admin-email" className="eyebrow mb-2 block">Email</label>
        <input
          id="admin-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email-input"
          className="mb-6 w-full border-b border-[#d4d4d4] bg-transparent pb-2 text-[#0A0A0A] outline-none transition-colors focus:border-[#0A0A0A]"
        />
        <label htmlFor="admin-password" className="eyebrow mb-2 block">Password</label>
        <input
          id="admin-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="mb-8 w-full border-b border-[#d4d4d4] bg-transparent pb-2 text-[#0A0A0A] outline-none transition-colors focus:border-[#0A0A0A]"
        />
        {error && <p data-testid="admin-login-error" className="mb-4 font-mono-accent text-xs text-[#8B0000]">{error}</p>}
        <button
          type="submit"
          disabled={state === "loading"}
          data-testid="admin-login-submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0A0A0A] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#333] disabled:opacity-60"
        >
          {state === "loading" ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
        </button>
        <a href="/" className="eyebrow mt-6 flex items-center justify-center gap-2 transition-colors hover:text-[#0A0A0A]">
          <ArrowLeft size={12} /> Back to site
        </a>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    Promise.all([
      axios.get(`${API}/prebookings`, { headers }),
      axios.get(`${API}/prebookings/summary`, { headers }),
    ])
      .then(([a, b]) => {
        setRows(a.data);
        setSummary(b.data);
      })
      .catch((err) => {
        if (err?.response?.status === 401) logout();
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (!token) return <Login onAuth={setToken} />;

  return (
    <div className="min-h-screen bg-[#FBFBF9] px-6 py-10 md:px-12">
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Watch size={18} strokeWidth={1.5} />
            <span className="font-serif-display text-xl text-[#0A0A0A]">Transcendence · Pre-bookings</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/" data-testid="admin-back-link" className="eyebrow flex items-center gap-2 transition-colors hover:text-[#0A0A0A]">
              <ArrowLeft size={13} /> Back to site
            </a>
            <button onClick={logout} data-testid="admin-logout" className="eyebrow flex items-center gap-2 transition-colors hover:text-[#0A0A0A]">
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>

        <div className="mb-10 grid grid-cols-2 gap-px overflow-hidden rounded-[2px] border border-[#0A0A0A]/8 bg-[#0A0A0A]/8 sm:grid-cols-4 lg:grid-cols-7">
          <div className="bg-white p-6" data-testid="summary-total">
            <p className="eyebrow">Total reserved</p>
            <p className="mt-2 font-serif-display text-4xl [font-variant-numeric:lining-nums_tabular-nums] text-[#0A0A0A]">{summary ? summary.display_total : "—"}</p>
            <p className="mt-1 font-mono-accent text-[10px] text-[#A3A3A3]">{summary ? `${summary.total_real} real` : ""}</p>
          </div>
          {COLORS.map((c) => (
            <div key={c.name} className="bg-white p-6" data-testid={`summary-${c.name.toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: c.swatch, border: c.name === "White" ? "1px solid #d4d4d4" : "none" }} />
                <p className="eyebrow">{c.name}</p>
              </div>
              <p className="mt-2 font-serif-display text-4xl [font-variant-numeric:lining-nums_tabular-nums] text-[#0A0A0A]">{summary ? summary.by_color[c.name] : "—"}</p>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-[2px] border border-[#0A0A0A]/8 bg-white">
          <div className="grid grid-cols-12 border-b border-[#0A0A0A]/8 px-6 py-4">
            <span className="col-span-6 eyebrow">Email</span>
            <span className="col-span-3 eyebrow">Colour</span>
            <span className="col-span-3 eyebrow text-right">Date</span>
          </div>
          {loading && <div className="px-6 py-10 text-center text-[#A3A3A3]">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <Mail size={22} className="text-[#A3A3A3]" />
              <p className="text-[#525252]">No pre-bookings yet. Share your landing page to start capturing interest.</p>
            </div>
          )}
          {rows.map((r) => {
            const c = COLORS.find((x) => x.name === r.color);
            return (
              <div key={r.id} data-testid={`row-${r.email}`} className="grid grid-cols-12 items-center border-b border-[#0A0A0A]/6 px-6 py-4 last:border-0">
                <span className="col-span-6 truncate text-[#0A0A0A]">{r.email}</span>
                <span className="col-span-3 flex items-center gap-2 text-[#525252]">
                  <span className="h-3 w-3 rounded-full" style={{ background: c?.swatch, border: r.color === "White" ? "1px solid #d4d4d4" : "none" }} />
                  {r.color}
                </span>
                <span className="col-span-3 text-right font-mono-accent text-xs text-[#A3A3A3]">
                  {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
